/**
 * The tutorial, in the app rather than a README nobody reads. It opens from the empty
 * state and from the failures it explains — a CORS error is the moment someone needs
 * the CORS policy, not a week later.
 */
export function Guide({ onClose }: { onClose: () => void }) {
  return (
    <div class="guide-wrap" data-testid="guide" onClick={onClose}>
      <div class="guide" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>Getting your own imagery in</h2>
          <button class="btn" onClick={onClose}>Close</button>
        </header>

        <section>
          <h3>What this needs</h3>
          <p>
            A <b>Cloud Optimized GeoTIFF</b> — an ordinary GeoTIFF with two extra
            properties: it is tiled internally, and it carries reduced-resolution
            copies of itself. Together those let a browser fetch the few hundred
            kilobytes covering your screen rather than the whole file. A 4&nbsp;GB
            ortho opens in about a second.
          </p>
          <p>
            Pix4D, Agisoft and most drone software write plain GeoTIFFs, so a file
            straight out of processing usually needs converting first. It is one
            command and it is not destructive.
          </p>
        </section>

        <section>
          <h3>Converting a file</h3>
          <p>
            {/* Name it explicitly: a bare `download` renders as download="true",
                and the browser then saves the file as "true". */}
            <a href="/cogify.py" download="cogify.py">Download <code>cogify.py</code></a>. If you
            have <a href="https://docs.astral.sh/uv/" target="_blank" rel="noreferrer">uv</a>,
            there is nothing to install — it reads its own dependencies:
          </p>
          <pre>uv run cogify.py my_ortho.tif</pre>
          <p>
            That inspects and changes nothing. It reports what is wrong: not tiled, no
            overviews, a compression browsers cannot decode, an undeclared nodata fill.
            To actually convert, give it an output path:
          </p>
          <pre>uv run cogify.py my_ortho.tif out.tif --mask</pre>
          <p>Or without uv: <code>pip install rasterio</code>, then <code>python cogify.py …</code>.</p>
        </section>

        <section>
          <h3>Naming the bands — the part that matters</h3>
          <p>
            Converting is the easy half. The half that changes your results is writing
            down <b>which band is which</b>. Nothing in a plain GeoTIFF says band 4 is
            near-infrared, so a viewer has to guess from the band count — and five
            bands is genuinely ambiguous. MicaSense RedEdge writes
            {' '}<b>B,&nbsp;G,&nbsp;R,&nbsp;NIR,&nbsp;RedEdge</b>; DJI P4 Multispectral
            writes <b>B,&nbsp;G,&nbsp;R,&nbsp;RedEdge,&nbsp;NIR</b>. Same count, bands
            4 and 5 swapped. Guess wrong and you compute NDRE while believing it is
            NDVI, with no error anywhere.
          </p>
          <pre>uv run cogify.py in.tif out.tif --mask \
  --names blue,green,red,nir,rededge</pre>
          <p>
            Do that once and the file explains itself to any tool that reads it,
            forever. If your data is scaled — reflectance stored as integers, say —
            record that too, and indices come out in real units:
          </p>
          <pre>uv run cogify.py in.tif out.tif --scale 0.00002 --offset -0.1</pre>
          <p>
            You can always set band roles by hand in the sidebar, but that is per
            session and per person. Writing them into the file is not.
          </p>
        </section>

        <section>
          <h3>Putting it somewhere this can read it</h3>
          <p>
            Loading by URL needs the host to do two things: answer <b>byte-range
            requests</b>, and — if the file is on a different domain from this page —
            send <b>CORS</b> headers. S3 and most object stores do ranges already. CORS
            you have to switch on:
          </p>
          <pre>{`[{
  "AllowedOrigins": ["*"],
  "AllowedMethods": ["GET", "HEAD"],
  "AllowedHeaders": ["Range"],
  "ExposeHeaders": ["Content-Range", "Content-Length", "Accept-Ranges"]
}]`}</pre>
          <p>
            <b>ExposeHeaders is the one people miss.</b> Without it the file's headers
            load fine and every tile read fails, which looks like a corrupt file rather
            than a settings problem.
          </p>
          <p>
            If CloudFront or another CDN sits in front, do not enable compression for
            <code> image/tiff</code>: a compressed response cannot be byte-ranged, and
            ranges are the entire point.
          </p>
          <p>
            None of this applies to <b>Open local file</b>, which reads straight off
            your disk. If a URL is misbehaving, open the same file locally to find out
            whether the problem is the file or the hosting.
          </p>
        </section>

        <section>
          <h3>Checking it worked</h3>
          <p>
            Load it here and look at the sidebar. Under <b>Bands</b>, each band should
            say <span class="tag metadata">metadata</span> rather than
            {' '}<span class="tag guess">guess</span>. Then hover the map: the spectral
            signature should dip in red and jump across the red edge over anything
            green. A curve that does not is the fastest sign the band order is wrong.
          </p>
        </section>
        <section>
          <h3>Still stuck?</h3>
          <p>
            Mail <a href="mailto:info@spectralmapper.com">info@spectralmapper.com</a>.
            A link to the file, or the output of <code>cogify.py</code> run on it,
            usually answers it fastest.
          </p>
        </section>
      </div>
    </div>
  );
}